/**
 * Recent Files List Component
 * Displays recently accessed charts in File menu
 */

import { devError } from '../../../utils/devLog';
import React, { useState, useEffect, useCallback } from 'react';
import {
  MenuItem,
  MenuDivider,
  Text,
  VStack,
  HStack,
  Spinner,
  Box
} from '@chakra-ui/react';
import { ChartMetadata } from '../../../models/storage';
import { getStorageService } from '../../../services/StorageService';
import { formatRelativeTime } from '../../../utils/datetime/formatters';
import { globalEventStream } from '../../common/AstrologyChart/universal_architecture/core/EventStream';
import { useMultipleCharts } from '../../common/AstrologyChart/MultipleChartsContext';
import './RecentFilesList.css';

interface RecentFilesListProps {
  onFileSelect: (id: string, metadata: ChartMetadata) => void;
  maxFiles?: number;
}


/**
 * Component that renders recent files in File menu
 */
const RecentFilesList: React.FC<RecentFilesListProps> = ({ 
  onFileSelect, 
  maxFiles = 10 
}) => {
  const [recentFiles, setRecentFiles] = useState<ChartMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [storageReady, setStorageReady] = useState(false);

  // Get loaded charts for display name
  const { charts, loadedChartIds } = useMultipleCharts();

  // Check storage readiness and load files
  const loadRecentFiles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const storageService = getStorageService();
      
      // Wait a bit for storage to initialize if needed
      let attempts = 0;
      while (!storageService.isInitialized() && attempts < 10) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
      
      if (!storageService.isInitialized()) {
        setStorageReady(false);
        setError('Storage not available');
        return;
      }

      setStorageReady(true);

      const result = await storageService.getRecentCharts(maxFiles);
      if (result.success && result.data) {
        // result.data.forEach((file, index) => {
        //   // Check the currently active chart (most recent one)
        //   if (index === 0) {
        //   }
        // });
        setRecentFiles(result.data);
      } else {
        devError('❌ Failed to load recent files:', result.error);
        setError(result.error || 'Failed to load recent files');
      }
    } catch (err) {
      devError('Error loading recent files:', err);
      setError('Failed to load recent files');
    } finally {
      setLoading(false);
    }
  }, [maxFiles]);

  // Load recent files on component mount and when storage becomes ready
  useEffect(() => {
    const storageService = getStorageService();
    
    // Only load immediately if storage is already ready
    if (storageService.isInitialized()) {
      setStorageReady(true);
      loadRecentFiles();
    }
    
    // Subscribe to storage ready event
    const unsubscribeReady = storageService.onReady(() => {
      if (!storageReady) {
        setStorageReady(true);
        loadRecentFiles();
      }
    });

    // Subscribe to recent files events
    const unsubscribeRecentFiles = globalEventStream.subscribe('storage.recent-files.updated', () => {
      loadRecentFiles();
    });

    // Subscribe to individual chart events for more granular updates
    const unsubscribeChartEvents = globalEventStream.subscribe('storage.chart.accessed', () => {
      loadRecentFiles();
    });

    // NOTE: chart.data.updated subscription removed - too broad
    // chart.field.renamed already covers name/place changes

    // Subscribe to field renames (name/place changes)
    const unsubscribeFieldRenamed = globalEventStream.subscribe('chart.field.renamed', () => {
      loadRecentFiles();
    });

    return () => {
      unsubscribeReady();
      unsubscribeRecentFiles.unsubscribe();
      unsubscribeChartEvents.unsubscribe();
      unsubscribeFieldRenamed.unsubscribe();
    };
  }, [loadRecentFiles, storageReady]);

  const handleFileClick = async (metadata: ChartMetadata) => {
    try {
      await onFileSelect(metadata.id, metadata);
      // Reload recent files to update access times
      loadRecentFiles();
    } catch (error) {
      devError('Error handling file click:', error);
    }
  };

  // Get display name from loaded chart (chart_name) or fallback to metadata name
  const getDisplayName = (metadata: ChartMetadata): string => {
    const loadedChart = charts.get(metadata.id);
    return loadedChart?.apiResponse?.basic_info?.chart_name || metadata.name;
  };

  // Get display location from loaded chart (place_name) or fallback to metadata location
  const getDisplayLocation = (metadata: ChartMetadata): string | undefined => {
    const loadedChart = charts.get(metadata.id);
    return loadedChart?.apiResponse?.basic_info?.place_name || metadata.chartDateTime?.location;
  };

  // Group files by date
  const groupFilesByDate = (files: ChartMetadata[]) => {
    const groups: { [key: string]: ChartMetadata[] } = {};
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    files.forEach(file => {
      const fileDate = new Date(file.accessed);
      const fileDateStr = fileDate.toDateString();
      const todayStr = today.toDateString();
      const yesterdayStr = yesterday.toDateString();
      
      let groupKey: string;
      if (fileDateStr === todayStr) {
        groupKey = 'Today';
      } else if (fileDateStr === yesterdayStr) {
        groupKey = 'Yesterday';
      } else {
        groupKey = fileDate.toLocaleDateString('en-US', { 
          weekday: 'long', 
          month: 'short', 
          day: 'numeric' 
        });
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(file);
    });
    
    // Sort groups by date (most recent first)
    const sortedGroups = Object.entries(groups).sort(([a], [b]) => {
      if (a === 'Today') return -1;
      if (b === 'Today') return 1;
      if (a === 'Yesterday') return -1;
      if (b === 'Yesterday') return 1;
      return new Date(b).getTime() - new Date(a).getTime();
    });
    
    return sortedGroups;
  };

  // Don't render anything if storage is definitively not available
  if (!loading && !storageReady && error === 'Storage not available') {
    return null;
  }

  return (
    <>
      <MenuDivider />
      
      {loading && (
        <MenuItem className="recent-files-loading">
          <HStack spacing={2}>
            <Spinner size="xs" className="recent-files-loading-icon" />
            <Text className="recent-files-loading-text">Loading recent files...</Text>
          </HStack>
        </MenuItem>
      )}
      
      {!loading && error && error !== 'Storage not available' && (
        <MenuItem className="recent-files-error">
          <Text className="recent-files-error-text">{error}</Text>
        </MenuItem>
      )}
      
      {!loading && storageReady && recentFiles.length === 0 && (
        <MenuItem className="recent-files-empty">
          <Text className="recent-files-empty-text">
            No recent files
          </Text>
        </MenuItem>
      )}
      
      {!loading && storageReady && recentFiles.length > 0 && (
        <>
          <Box className="recent-files-header">
            <Text className="recent-files-title">
              Recent Files
            </Text>
          </Box>
          
          {groupFilesByDate(recentFiles).map(([groupName, files]) => (
            <React.Fragment key={groupName}>
              <Box className="date-group-header">
                <Text className="date-group-title">
                  {groupName}
                </Text>
              </Box>
              
              {files.map((file) => {
                const isOpen = loadedChartIds.includes(file.id);
                return (
                <MenuItem
                  key={file.id}
                  className={`recent-file-item ${isOpen ? 'is-open' : ''}`}
                  onClick={() => handleFileClick(file)}
                >
                  <VStack className="recent-file-content">
                    <HStack className="recent-file-header">
                      <HStack className="recent-file-name-container">
                        <Text className="recent-file-name">
                          {getDisplayName(file)}
                        </Text>
                        {isOpen && (
                          <Text className="recent-file-current-badge">
                            ●
                          </Text>
                        )}
                      </HStack>
                      <Text className="recent-file-time">
                        {formatRelativeTime(file.accessed)}
                      </Text>
                    </HStack>
                    
                    {file.chartDateTime && (
                      <Text className="recent-file-details">
                        {file.chartDateTime.date} {file.chartDateTime.time}
                        {getDisplayLocation(file) && ` • ${getDisplayLocation(file)}`}
                      </Text>
                    )}
                  </VStack>
                </MenuItem>
              );
              })}
            </React.Fragment>
          ))}
        </>
      )}
    </>
  );
};

export default RecentFilesList;
